<script setup lang="ts">
import { computed } from 'vue'

import { useAuth0 } from '@auth0/auth0-vue'
import { ChevronsUpDown, LogOut } from '@lucide/vue'
import { RouterLink, useRoute } from 'vue-router'

import { Avatar, AvatarFallback } from '#ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '#ui/sidebar'

import AppEnvironmentSwitcher from './AppEnvironmentSwitcher.vue'
import AppThemeToggle from './AppThemeToggle.vue'
import { NAV } from './navigation'

const { user, logout } = useAuth0()
const route = useRoute()

const isActive = (to: string) => route.path.startsWith(to)

const initials = computed(() => (user.value?.email ?? '?').slice(0, 2).toUpperCase())

function handleLogout() {
  logout({ logoutParams: { returnTo: window.location.origin } })
}
</script>

<template>
  <Sidebar collapsible="icon" class="group-data-[side=left]:border-r-0">
    <SidebarHeader class="h-14 justify-center">
      <AppEnvironmentSwitcher />
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup v-for="group in NAV" :key="group.label">
        <SidebarGroupLabel class="group-data-[collapsible=icon]:mt-0">
          {{ group.label }}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in group.items" :key="item.to">
              <SidebarMenuButton as-child :is-active="isActive(item.to)" :tooltip="item.label">
                <RouterLink :to="item.to">
                  <component :is="item.icon" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter>
      <div class="flex items-center gap-1">
        <SidebarMenu class="flex-1">
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <SidebarMenuButton tooltip="Account">
                  <Avatar class="size-5 rounded-sm">
                    <AvatarFallback class="rounded-sm text-[10px]">{{ initials }}</AvatarFallback>
                  </Avatar>
                  <span class="truncate">{{ user?.email }}</span>
                  <ChevronsUpDown class="ml-auto size-3.5 opacity-60" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" class="w-56">
                <DropdownMenuLabel class="truncate font-normal text-muted-foreground">
                  {{ user?.email }}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem @select="handleLogout">
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
        <AppThemeToggle class="group-data-[collapsible=icon]:hidden" />
      </div>
    </SidebarFooter>
  </Sidebar>
</template>
